import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  LayoutDashboard, Wallet, LogOut, Download, Sprout, Mountain, Coins, Receipt,
  Scale, Target, Menu,
} from "lucide-react";
import { API_BASE } from "../lib/api";

const NAV_ITEMS = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
  { to: "/debts", label: "Debts", icon: Wallet, testId: "nav-debts" },
  { to: "/activity", label: "Activity", icon: Receipt, testId: "nav-activity" },
  { to: "/assets", label: "Assets", icon: Coins, testId: "nav-assets" },
  { to: "/networth", label: "Net worth", icon: Scale, testId: "nav-networth" },
  { to: "/plan", label: "Plan", icon: Target, testId: "nav-plan" },
  { to: "/strategy", label: "Strategy", icon: Mountain, testId: "nav-strategy" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("dt_token");
    const res = await fetch(`${API_BASE}/export/csv`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "debt_tracker_export.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
      isActive
        ? "bg-[#2C4A3B] text-white shadow-sm"
        : "text-[#5C635A] hover:bg-[#EEEBE2] hover:text-[#1D3328]"
    }`;

  const sheetLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
      isActive
        ? "bg-[#2C4A3B] text-white shadow-sm"
        : "text-[#1D3328] hover:bg-[#EEEBE2]"
    }`;

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3 sm:gap-4">
          {/* Logo */}
          <div className="flex items-center gap-2.5 min-w-0 shrink-0">
            <div className="w-9 h-9 rounded-xl bg-[#2C4A3B] flex items-center justify-center text-white shrink-0">
              <Sprout className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <div className="min-w-0">
              <div className="font-display text-lg font-medium leading-tight text-[#1D3328]">
                Quietus
              </div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#8B9389] -mt-0.5 hidden sm:block">
                Debt Tracker
              </div>
            </div>
          </div>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-1 min-w-0 flex-1 justify-center">
            {NAV_ITEMS.map(({ to, label, icon: Icon, end, testId }) => (
              <NavLink key={to} to={to} end={end} className={linkClass} data-testid={testId}>
                <Icon className="w-4 h-4 shrink-0" strokeWidth={1.7} />
                <span className="whitespace-nowrap">{label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              className="rounded-xl border-[#E5E3DB] hover:bg-[#EEEBE2] text-[#1D3328] hidden 2xl:flex"
              data-testid="export-csv-button"
            >
              <Download className="w-4 h-4 mr-1.5" strokeWidth={1.7} />
              Export CSV
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="rounded-xl text-[#5C635A] hidden xl:inline-flex"
              data-testid="logout-button"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.7} />
            </Button>

            {/* Hamburger — shown below xl breakpoint */}
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-[#E5E3DB] xl:hidden h-9 w-9 p-0"
                  data-testid="mobile-menu-button"
                  aria-label="Open menu"
                >
                  <Menu className="w-5 h-5 text-[#1D3328]" strokeWidth={1.7} />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[85%] sm:w-80 bg-[#F9F8F6] border-l border-[#E5E3DB] p-0 flex flex-col"
                data-testid="mobile-menu-sheet"
              >
                <div className="px-5 pt-6 pb-4 border-b border-[#E5E3DB]">
                  <p className="text-[10px] tracking-[0.24em] uppercase text-[#8B9389]">
                    Signed in as
                  </p>
                  <p className="text-sm font-medium text-[#1D3328] truncate mt-0.5">
                    {user?.name}
                  </p>
                  <p className="text-xs text-[#8B9389] truncate">{user?.email}</p>
                </div>

                <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
                  {NAV_ITEMS.map(({ to, label, icon: Icon, end, testId }) => (
                    <NavLink
                      key={to}
                      to={to}
                      end={end}
                      onClick={() => setMenuOpen(false)}
                      className={sheetLinkClass}
                      data-testid={`${testId}-mobile`}
                    >
                      <Icon className="w-4 h-4 shrink-0" strokeWidth={1.7} />
                      <span>{label}</span>
                    </NavLink>
                  ))}
                </nav>

                <div className="border-t border-[#E5E3DB] p-3 space-y-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setMenuOpen(false);
                      exportCsv();
                    }}
                    className="w-full rounded-xl border-[#E5E3DB] hover:bg-[#EEEBE2] text-[#1D3328] justify-start"
                    data-testid="export-csv-button-mobile"
                  >
                    <Download className="w-4 h-4 mr-2" strokeWidth={1.7} />
                    Export CSV
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setMenuOpen(false);
                      handleLogout();
                    }}
                    className="w-full rounded-xl text-[#B04A32] hover:bg-[#F7E6DF] justify-start"
                    data-testid="logout-button-mobile"
                  >
                    <LogOut className="w-4 h-4 mr-2" strokeWidth={1.7} />
                    Sign out
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}
